import { Box, Center, Flex, Text } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import React, { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Route, Switch, useLocation } from "react-router-dom";
import { Redirect } from "react-router-dom/cjs/react-router-dom.min";
import { Spinner } from "../../components/Loader";
import Sidebar from "../../components/Sidebar";
import { dropAnimation } from "../../components/styles";
import { useAuth } from "../../helpers/configs/plugins/CookieAuthPlugin";

const Dashboard = lazy(() => import("./dashboard/Dashboard"));
const SaleOrder = lazy(() => import("./dashboard/SaleOrder"));
const SaleOrderPayback = lazy(() => import("./dashboard/SaleOrderPayback"));
const ReportSaleOrder = lazy(() => import("./report/SaleOrder"));
const ReportCustomerTransaction = lazy(() => import("./report/CustomerTransaction"));
const Company = lazy(() => import("./setting/company"));
const CustomerApp = lazy(() => import("./setting/customer-app"));
const Driver = lazy(() => import("./setting/driver"));
const Station = lazy(() => import("./setting/station"));
const Franchise = lazy(() => import("./setting/franchise"));
const ChargerAccount = lazy(() => import("./setting/charger-account"));
const ChargerPrice = lazy(() => import("./setting/charger-price"));
const SystemRole = lazy(() => import("./setting/system-role"));
const User = lazy(() => import("./setting/user"));
const ChangePassword = lazy(() => import("./change-password"));

const routeItem = [
  // * dashboard
  { path: "/dashboard", component: <Dashboard /> },
  { path: "/sale-order", component: <SaleOrder /> },
  { path: "/sale-order-payback", component: <SaleOrderPayback /> },

  // * report
  { path: "/report-sale-order", component: <ReportSaleOrder /> },
  { path: "/customer-transaction", component: <ReportCustomerTransaction /> },

  // * setting
  { path: "/customer-app", component: <CustomerApp /> },
  { path: "/company", component: <Company /> },
  { path: "/driver", component: <Driver /> },
  { path: "/station", component: <Station /> },
  { path: "/franchise", component: <Franchise /> },
  { path: "/charger-account", component: <ChargerAccount /> },
  { path: "/charger-price", component: <ChargerPrice /> },
  { path: "/system-role", component: <SystemRole /> },
  { path: "/user", component: <User /> },
  { path: "/change-password", component: <ChangePassword /> },
];

const MotionBox = motion(Box);
export default function App() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { pathname } = useLocation();
  const [moduleName, setModuleName] = useState({ name: "", action: "" });
  const { username } = useAuth()?.user || {};
  const isEn = i18n.language === "en";

  // * get module name from path
  useEffect(() => {
    const pathParts = pathname.split("/").filter(Boolean);
    if (pathParts.length === 0) {
      setModuleName({ name: "", action: "" });
      return;
    }

    const lastPart = pathParts[pathParts.length - 1];
    if (lastPart === "list") {
      const mainPath = pathParts[pathParts.length - 2];
      const pathSegment = mainPath?.replace(/-/g, "_") || "";
      setModuleName({ name: pathSegment, action: "" });
      return;
    }

    // Check if the last part is an ID (numeric)
    const isLastPartId = /^\d+$/.test(lastPart);
    const actionPart = isLastPartId ? pathParts[pathParts.length - 2] : lastPart;
    const mainPathIndex = isLastPartId ? pathParts.length - 3 : pathParts.length - 2;
    const mainPath = mainPathIndex >= 0 ? pathParts[mainPathIndex] : pathParts[0]; // Fallback to first segment if index is invalid
    const pathSegment = mainPath?.replace(/-/g, "_") || "";
    const validActions = ["add", "view", "edit", "reset", "clone"];
    if (validActions.includes(actionPart)) {
      setModuleName({ name: pathSegment, action: actionPart });
    } else {
      setModuleName({ name: pathSegment, action: "" });
    }
  }, [pathname]);

  return (
    <Flex position="relative" h="100vh" overflow="auto" id="app-container">
      <Box display="flex">
        <Sidebar />
      </Box>
      <Box w="calc(100% - 18rem)" position="relative" flex="1" overflow="auto">
        <Flex justifyContent="space-between" alignItems="center" h="40px" boxShadow="sm">
          <Text fontWeight="semibold" ml="4" fontSize="18px">
            {(moduleName.action && t("label." + moduleName?.action)) + (isEn ? " " : "") + (moduleName.name && t("label." + moduleName?.name))}
          </Text>
        </Flex>
        <Box h="calc(100vh - 40px)">
          <Suspense
            fallback={
              <Center h="80vh" w="full">
                <Spinner />
              </Center>
            }
          >
            <AnimatePresence mode="wait">
              <Switch location={location}>
                {routeItem.map(({ path, component }) => {
                  // super admin can't change password
                  if (path === "/change-password" && username === "super_admin") return <Redirect to="/" />;
                  return (
                    <Route key={path} path={path}>
                      <MotionBox overflow="auto" px="2" pt="2" pb="1" {...dropAnimation} key={location.pathname}>
                        {component}
                      </MotionBox>
                    </Route>
                  );
                })}
              </Switch>
            </AnimatePresence>
          </Suspense>
        </Box>
      </Box>
    </Flex>
  );
}
